import { useCallback } from 'react';

import type { Node } from '@xyflow/react';

import { emitRecipeModified } from '../events/recipeEditorEvents';
import { generateID } from '../utils/generateId';
import { generateNodeName } from '../utils/generateNodeName';
import { nodeUtils } from '../utils/nodeUtils';

interface UseFlowDragDropOptions {
  type: string | null;
  setType: (type: string | null) => void;
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  screenToFlowPosition: (position: { x: number; y: number }) => { x: number; y: number };
  debouncedUpdateRecipe: () => void;
}

/**
 * Custom hook for handling drag and drop operations in ReactFlow
 */
export const useFlowDragDrop = ({
  type,
  setType,
  setNodes,
  screenToFlowPosition,
  debouncedUpdateRecipe,
}: UseFlowDragDropOptions) => {
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (!type) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const id = generateID(type);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Node data is dynamic based on node type
      const nodeData: any = {
        '@id': id,
        name: generateNodeName(type),
        type,
      };

      // Add default function for task nodes
      if (nodeUtils.getNodeType({ data: nodeData, type }) === 'task') {
        nodeData.function = nodeUtils.getTaskNodeFunction({ data: nodeData, type });
      }

      const newNode = {
        id,
        type,
        position,
        data: nodeData,
      };

      setNodes((nds) => nds.concat(newNode));
      setType(null);

      // Emit event: node added via drag-drop (explicit user action — bypass counter)
      emitRecipeModified({ force: true });
      debouncedUpdateRecipe();
    },
    [type, setNodes, setType, screenToFlowPosition, debouncedUpdateRecipe]
  );

  return {
    onDragOver,
    onDrop,
  };
};
